import {Box, styled, Tooltip} from "@mui/material";
import React, {useEffect, useState} from "react";
import AIDoingThings from "../../assets/AIDoingThings.png";
import {AIUseIcon} from "../../assets/AIUseIcon";
import {getRandom} from "../../helpers/gerRandom";
import {initDiagramTrendWithAiPower} from "../../helpers/initDiagramTrendWithAiPower";
import {getDateStr} from "../../helpers/formatDate";
import {generateDates, generateDatesFuture} from "../../helpers/generateDates";
import {TradingBlockTopButtonEl} from "./UseCaseTradingBlock";

interface LinearDiagramInterface {
    id: string;
    generateFor: number;
    generateFuture: number;
    height: string;
}

const LinearDiagramContainer = styled(Box)({
    position: "relative",
    height: "100%",
    border: "1px solid #808080",
    borderRadius: "8px",
});

const DiagramEl = styled(Box)({
    width: "100%",
});

const AIMenuImg = styled(Box)({
    background: `url(${AIDoingThings}) no-repeat center`,
    backgroundSize: "contain",
    height: "100%",
    width: "100%",
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 3
});

const LoadingAnimationContainer = styled(Box)({
    position: "absolute",
    height: "100%",
    width: "auto",
    aspectRatio: "1/1",
    overflow: "hidden",
});

const LoadingContainer = styled(Box)({
    width: "100%",
    position: "absolute",
    background: "#2F2F2F",
    overflow: "hidden",
    top: 0,
    left: 0,
    display: "flex",
    justifyContent: "center"
});

const AIMenuAnimation1 = styled(Box)({
    background: "#8B0000",
    height: "3px",
    width: "200px",
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 2,
    boxShadow: "0 0 70px 50px #8B0000",
    animation: "outer-animation1 6s linear infinite",
    "@keyframes outer-animation1": {
        "0%": {
            transform: "translate(0, 0) rotate(0deg)",
        },
        "25%": {
            transform: "translate(200px, 100px) rotate(560deg)"
        },
        "50%": {
            transform: "translate(100px, 300px) rotate(1360deg)"
        },
        "75%": {
            transform: "translate(-50px, 100px) rotate(1800deg)"
        },
        "100%": {
            transform: "translate(0, 0) rotate(2000deg)"
        }
    },
});

const AIMenuAnimation2 = styled(Box)({
    background: "#8B0000",
    height: "3px",
    width: "200px",
    position: "absolute",
    bottom: 0,
    right: 0,
    zIndex: 2,
    boxShadow: "0 0 70px 50px #8B0000",
    animation: "outer-animation2 6s .1s linear infinite",
    "@keyframes outer-animation2": {
        "0%": {
            transform: "translate(0, 0) rotate(0deg)",
        },
        "25%": {
            transform: "translate(-200px, -100px) rotate(-560deg)"
        },
        "50%": {
            transform: "translate(-100px, -300px) rotate(-1360deg)"
        },
        "75%": {
            transform: "translate(50px, -100px) rotate(-1800deg)"
        },
        "100%": {
            transform: "translate(0, 0) rotate(-2300deg)"
        }
    },
});

const AIBlock = styled(Box)({
    position: "absolute",
    right: 0,
    top: 0
});

const LinearDiagram = ({id, generateFor, generateFuture, height}: LinearDiagramInterface):JSX.Element => {
    
    const [loading, setLoading] = useState<boolean>(false);
    
    const realData = generateDates(generateFor).reverse().map((opt) => getDateStr(opt));
    
    const futureData = generateDatesFuture(generateFuture).slice(1).map((opt) => getDateStr(opt));
    
    const realNumbers = [...new Array((realData.length + futureData.length) - (generateFuture - 1)).fill(0).map(() => {
        return getRandom(55, 470);
    }).sort((a,b) => a-b)];
    
    const predictGrow = [...new Array((realData.length + futureData.length) - generateFuture).fill(0),realNumbers.at(-1), ...new Array(generateFuture).fill(0).map(() => {
        return getRandom(450, 650);
    }).sort((a,b) => a-b)];
    
    const predictDrop = [...new Array((realData.length + futureData.length) - generateFuture).fill(0),realNumbers.at(-1), ...new Array(generateFuture).fill(0).map(() => {
        return getRandom(450, 320);
    }).sort((a,b) => b-a)];
    
    const predictStable = [...new Array((realData.length + futureData.length) - generateFuture).fill(0),realNumbers.at(-1), ...new Array(generateFuture).fill(0).map(() => {
        return getRandom(450, 520);
    }).sort((a,b) => a-b)];
    
    useEffect(() => {
        initDiagramTrendWithAiPower(id, realData, [], realNumbers, [], [], []);
    },[]);
    
    const handleAI = ():void => {
        setLoading(true);
        setTimeout(() => {
            setLoading(false);
            initDiagramTrendWithAiPower(id, realData, futureData, realNumbers, predictGrow, predictStable, predictDrop);
        },2000);
    };
    
    
    const renderLoadingBlock = ():React.ReactNode => {
        if(loading){
            return <LoadingContainer
                sx={{
                    height: height
                }}
            >
                <LoadingAnimationContainer>
                    <AIMenuImg/>
                    <AIMenuAnimation1/>
                    <AIMenuAnimation2/>
                </LoadingAnimationContainer>
            </LoadingContainer>;
        }
        return null;
    };
    
    const renderAIBlock = ():React.ReactNode => {
        return <AIBlock>
            <Tooltip title={"Calculate possible probabilities for your investment portfolio for the next 10 days. Using market analysis produced by our AI"}>
                <TradingBlockTopButtonEl
                    onClick={handleAI}
                >
                    <AIUseIcon color={"#FFFFFF"}/>
                </TradingBlockTopButtonEl>
            </Tooltip>
        </AIBlock>;
    };
    
    return (
        <LinearDiagramContainer>
            <DiagramEl id={id} sx={{
                height
            }}/>
            {renderAIBlock()}
            {renderLoadingBlock()}
        </LinearDiagramContainer>
    );
};

export default LinearDiagram;
